import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Returns a callback ref and a boolean indicating whether the element's width
 * is below the given breakpoint. Uses ResizeObserver to track actual element width,
 * which works correctly in Chrome split view unlike viewport-based media queries.
 */
export function useContainerBreakpoint(breakpointPx: number): {
  ref: (node: HTMLElement | null) => void;
  isBelow: boolean | null;
} {
  const [isBelow, setIsBelow] = useState<boolean | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (node) {
        setIsBelow(node.clientWidth < breakpointPx);

        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const size = entry.contentBoxSize[0];
            if (size) {
              setIsBelow(size.inlineSize < breakpointPx);
            }
          }
        });
        observer.observe(node);
        observerRef.current = observer;
      }
    },
    [breakpointPx]
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return { ref, isBelow };
}
